export interface AppUser {
  id: string
  email?: string | null
  fullName?: string | null
}

/** @deprecated alias — use AppUser */
export type AuthUser = AppUser

export interface AdminUser {
  id: string
  user_id: string
  role: 'admin' | 'super_admin'
  permissions: string[]
  created_at: string
  is_active: boolean
}

export interface Package {
  id: string
  name: string
  description: string
  price: number
  duration_days: number
  features: string[]
  package_type: 'in_person' | 'online' | 'meal_plan'
  is_active: boolean
  created_at: string
  sessions_included?: number
}

export interface UserPackage {
  id: string
  user_id: string
  package_id: string
  status: 'active' | 'expired' | 'cancelled'
  purchase_date: string
  expiry_date: string
  sessions_remaining?: number
  stripe_subscription_id?: string
  packages?: { name: string; package_type: string }
  tracker_url?: string
  tracker_type?: string
  includes_training?: boolean
  competition_prep?: boolean
}

export interface TrainingSession {
  id: string
  user_id: string
  trainer_id?: string
  package_id?: string
  scheduled_date: string
  duration_minutes: number
  session_type: 'in_person' | 'online' | 'meal_plan'
  status: 'scheduled' | 'completed' | 'cancelled'
  notes?: string
  meal_plan_weeks?: number
  pickup_delivery_info?: string
  client_join_url?: string
  admin_joined?: boolean
  completed_at?: string
  client_full_name?: string
  client_email?: string
  updated_at?: string
}

export interface ClientRecommendation {
  id: string
  user_id: string
  non_training_day_plan?: string[]
  personal_note?: string
  icon_colors?: string[]
  created_at?: string
  updated_at?: string
}
export interface UserPreference {
  id: string
  user_id: string
  email: string
  full_name: string
  phone?: string
  location?: string
  date_of_birth?: string
  fitness_goals?: string[]
  workout_time?: string
  preferred_workout_time?: string
  avatar_url?: string
  notification_preferences?: Record<string, boolean>
  last_login?: string
  created_at?: string
  updated_at?: string
}
/** Map Instant camelCase rows to the snake_case shapes pages already expect */
export function mapPackage(row: any): Package {
  return {
    id: row.id,
    name: row.name,
    description: row.description || '',
    price: row.price,
    duration_days: row.durationDays,
    features: Array.isArray(row.features) ? row.features : [],
    package_type: row.packageType,
    is_active: !!row.isActive,
    created_at: toIso(row.createdAt),
    sessions_included: row.sessionsIncluded,
  }
}

export function mapUserPackage(row: any): UserPackage {
  return {
    id: row.id,
    user_id: row.userId,
    package_id: row.packageId,
    status: row.status,
    purchase_date: toIso(row.purchaseDate),
    expiry_date: toIso(row.expiryDate),
    sessions_remaining: row.sessionsRemaining,
    stripe_subscription_id: row.stripeSubscriptionId,
    packages: row.packageName
      ? { name: row.packageName, package_type: row.packageType || '' }
      : row.package
        ? { name: row.package.name, package_type: row.package.packageType }
        : undefined,
    tracker_url: row.trackerUrl,
    tracker_type: row.trackerType,
    includes_training: !!row.includesTraining,
    competition_prep: !!row.competitionPrep,
  }
}

export function mapSession(row: any): TrainingSession {
  return {
    id: row.id,
    user_id: row.userId,
    trainer_id: row.trainerId,
    package_id: row.packageId,
    scheduled_date: toIso(row.scheduledDate),
    duration_minutes: row.durationMinutes,
    session_type: row.sessionType,
    status: row.status,
    notes: row.notes,
    meal_plan_weeks: row.mealPlanWeeks,
    pickup_delivery_info: row.pickupDeliveryInfo,
    client_join_url: row.clientJoinUrl,
    admin_joined: row.adminJoined,
    completed_at: row.completedAt ? toIso(row.completedAt) : undefined,
    client_full_name: row.clientFullName,
    client_email: row.clientEmail,
    updated_at: row.updatedAt ? toIso(row.updatedAt) : undefined,
  }
}

export function mapPreference(row: any): UserPreference {
  const goals = Array.isArray(row.fitnessGoals) ? row.fitnessGoals : []
  return {
    id: row.id,
    user_id: row.userId,
    email: row.email,
    full_name: row.fullName,
    phone: row.phone,
    location: row.location,
    date_of_birth: row.dateOfBirth,
    fitness_goals: goals,
    workout_time: row.workoutTime,
    preferred_workout_time: row.workoutTime,
    avatar_url: row.avatarUrl,
    notification_preferences: row.notificationPreferences || undefined,
    last_login: row.lastLogin ? toIso(row.lastLogin) : undefined,    
    created_at: row.createdAt ? toIso(row.createdAt) : undefined,
    updated_at: row.updatedAt ? toIso(row.updatedAt) : undefined,
  }
}

export function mapRecommendation(row: any): ClientRecommendation {
  return {
    id: row.id,
    user_id: row.userId,
    non_training_day_plan: Array.isArray(row.nonTrainingDayPlan)
      ? row.nonTrainingDayPlan
      : [],
    personal_note: row.personalNote,
    icon_colors: Array.isArray(row.iconColors) ? row.iconColors : [],
    created_at: row.createdAt ? toIso(row.createdAt) : undefined,
    updated_at: row.updatedAt ? toIso(row.updatedAt) : undefined,
  }
}

function toIso(value: any): string {
  if (value == null) return ''
  if (typeof value === 'number') return new Date(value).toISOString()
  if (typeof value === 'string') {
    const n = Date.parse(value)
    return Number.isNaN(n) ? value : new Date(n).toISOString()
  }
  return String(value)
}
